/**
 * The `orca-admin` design slug's scoped stylesheets as render-blocking head
 * links.
 *
 * A stylesheet the SSR markup depends on has to be a `<link>` in the head the
 * server sends (packages/web/CLAUDE.md → Performance budgets, rule 6). The
 * frames render from `.lazy.tsx` chunks, and TanStack's SSR manifest only
 * attributes CSS to chunks its own splitter produced, so a side-effect
 * import of `repo.css` from the components painted raw markup on a hard
 * reload and then jumped into place. The sheets arrive through `?url` imports
 * here instead, and the `/orca` layout route and the design board spread
 * these links into their `head()`.
 *
 * The board-only `fonts.css` (the capture's Google Fonts request) is NOT in
 * this list: only the design board links it, so it never ships with the
 * production `/orca` pages, which load their fonts through `config/head.ts`.
 *
 * @since 1.0.0
 */
import type { HeadLink } from "~/config/head.ts";

import archiveCss from "./archive.css?url";
import defaultsCss from "./defaults.css?url";
import fluidCss from "./fluid.css?url";
import onboardCss from "./onboard.css?url";
import operationsCss from "./operations.css?url";
import repoCss from "./repo.css?url";

/**
 * The slug's scoped sheets in the components' former import order (the
 * `shared.tsx` sheets, then the archive sheet), so the cascade is unchanged.
 *
 * @since 1.0.0
 */
export const ORCA_ADMIN_STYLESHEET_LINKS: Array<HeadLink> = [
  { rel: "stylesheet", href: repoCss },
  { rel: "stylesheet", href: operationsCss },
  { rel: "stylesheet", href: onboardCss },
  { rel: "stylesheet", href: defaultsCss },
  { rel: "stylesheet", href: fluidCss },
  { rel: "stylesheet", href: archiveCss },
];
